'use client'
import React, { useCallback, useMemo } from 'react'

const APPEND_FILE_SUFFIX_MATCH_DOMAIN = ['cdn.shopify.com', 'cdn.shopifycdn.net', 'www.facebook.com']

export interface PictureProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** picture 内部img 标签的类名 */
  imgClassName?: string
  /** 图片url , 支持传入不同分辨率下的图片，比如，格式 "url1 1920， url2 1080, url3 767" */
  source?: string
  /** 图片质量 */
  quality?: number
  /** 响应式图片，对应picture 下source 的max-width */
  deviceSizes?: number[]
  alt?: string
  loading?: 'eager' | 'lazy'
  className?: string
}
const images = {
  domains: ['cdn.shopify.com', 'cdn.shopifycdn.net', 'www.facebook.com'],
  deviceSizes: [375, 390, 768, 1024, 1280, 1440, 1540, 1920],
}

const isMatchDomain = (srcDomain: string) => {
  return APPEND_FILE_SUFFIX_MATCH_DOMAIN.some(domain => srcDomain == domain)
}
const idMatchFileExtensions = (fileName: string = '') => /\.(png|apng|jpg|jpeg|gif|svg|webp|tif)/.test(fileName)

/**
 * 从 URL 中解析 alt 参数
 * @param url 图片 URL
 * @returns 解析出的 alt 值，如果没有则返回空字符串
 */
const parseAltFromUrl = (url: string): string => {
  try {
    return new URL(url).searchParams.get('alt') || ''
  } catch {
    return ''
  }
}

/**
 * 移除 URL 中的 alt 参数
 * @param url 图片 URL
 * @returns 移除 alt 参数后的 URL
 */
const removeAltFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.delete('alt')
    return urlObj.toString()
  } catch {
    return url
  }
}

/**
 * Picture - 图片
 *
 * @description 响应式图片组件，支持多种分辨率、质量控制和自动alt文本解析功能
 */
const Picture = React.forwardRef<HTMLPictureElement, PictureProps>(
  (
    {
      imgClassName = '',
      source = '',
      quality = 2,
      deviceSizes = [390, 767, 1024, 1439, 1919],
      className,
      loading = 'lazy',
      alt = '',
      ...rest
    }: PictureProps,
    ref
  ) => {
    const isShopifycdn = (src: string | string[]) => {
      const domains = images?.domains || []
      return domains.some(domain => src.includes(domain))
    }

    const imgSrcSetsize = useCallback(({ src, width }: { src: string; width: number }) => {
      const size = width
      const cleanSrc = removeAltFromUrl(src)
      try {
        const parsedUrl = new URL(cleanSrc)
        if (!isMatchDomain(parsedUrl.hostname)) {
          return cleanSrc
        }
        const fileName = parsedUrl.pathname.split('/').pop() || ''
        if (idMatchFileExtensions(fileName)) {
          return cleanSrc
            ?.split('/')
            ?.map(item => {
              if (
                isShopifycdn(cleanSrc) &&
                /\.(png|apng|jpg|jpeg|gif|svg|webp|tif)(\?.*)?/.test(item) &&
                /_[0-9]{2,4}x/.test(cleanSrc) === false
              ) {
                const name = item.split('.')
                return name
                  .map((v, i) => {
                    if (i === name.length - 2) {
                      return `${v}_${size < 3960 ? size : 3980}x`
                    }
                    return v
                  })
                  .join('.')
              }
              return item
            })
            .join('/')
        }
      } catch (error) {
        return cleanSrc
      }
    }, [])

    const sourceSrcSet = useCallback(
      ({ srcArr, width }: { srcArr: string[][]; width: number }) => {
        const computedWidth = width * quality
        const defaultSrc = imgSrcSetsize({ src: srcArr[0][0], width: computedWidth })

        const matchingSrc = srcArr
          .filter(([, breakpoint]) => breakpoint && parseInt(breakpoint, 10) >= width)
          .map(([src]) => imgSrcSetsize({ src, width: computedWidth }))
          .pop()

        return <source key={width} srcSet={matchingSrc || defaultSrc} media={`(max-width: ${width}px)`} />
      },
      [imgSrcSetsize, quality]
    )

    const sourceList = useMemo(() => {
      if (typeof source !== 'string' || !source) {
        return { sources: [], defaultImg: undefined, parsedAlt: alt || '' }
      }

      const cleaned = source
        .replace(/[\r\n]/g, '')
        .replace(/\s\s+/g, ' ')
        .trim()

      // 基于HTTP协议识别URL边界的解析方案
      const parseSourceByHttpBoundary = (input: string): string[][] => {
        const result: string[][] = []

        // 找到所有http://或https://开头的位置
        const httpPositions: number[] = []
        const httpRegex = /https?:\/\//g
        let match
        while ((match = httpRegex.exec(input)) !== null) {
          httpPositions.push(match.index)
        }

        // 如果没找到http，fallback到原逻辑（兼容相对路径等）
        if (httpPositions.length === 0) {
          const fallback = input.split(',').map(item => item.split(' ').filter(Boolean))
          return fallback.filter(parts => parts.length > 0)
        }

        // 处理每个URL段
        for (let i = 0; i < httpPositions.length; i++) {
          const start = httpPositions[i]
          const end = i < httpPositions.length - 1 ? httpPositions[i + 1] : input.length

          let segment = input.slice(start, end).trim()

          // 移除末尾的逗号和空格
          segment = segment.replace(/\s*,\s*$/, '').trim()

          // 从右往左查找最后一个空格+数字的模式（尺寸参数）
          const sizeMatch = segment.match(/^(.+?)\s+(\d+)$/)

          if (sizeMatch) {
            // 有尺寸参数
            result.push([sizeMatch[1].trim(), sizeMatch[2]])
          } else {
            // 没有尺寸参数
            if (segment) result.push([segment])
          }
        }

        return result
      }

      const srcArr = parseSourceByHttpBoundary(cleaned)

      // 从 URL 中解析 alt 参数，优先使用第一个有 alt 参数的 URL
      const parsedAlt =
        alt ||
        srcArr
          .map(item => item[0])
          .filter(Boolean)
          .map(parseAltFromUrl)
          .find(Boolean) ||
        ''

      const sources = deviceSizes.map(width => sourceSrcSet({ srcArr, width }))
      const defaultImg = imgSrcSetsize({ src: srcArr[0][0], width: 1920 * quality })

      return { sources, defaultImg, parsedAlt }
    }, [deviceSizes, imgSrcSetsize, quality, source, sourceSrcSet, alt])

    const { sources, defaultImg, parsedAlt } = sourceList

    return (
      <picture ref={ref} className={`block overflow-hidden ${className}`}>
        {sources}
        {defaultImg && (
          <img
            key="default"
            src={defaultImg}
            className={`w-full ${imgClassName}`}
            loading={loading}
            alt={parsedAlt}
            {...rest}
          />
        )}
        {rest.children}
      </picture>
    )
  }
)

Picture.displayName = 'Picture'
export default Picture
